
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Stack, Typography } from "@mui/material";

import MainCard from "components/MainCard";
import EquipmentPartChainGraphs from "./chart/EquipmentPartChainGraphs";
import EquipmentPartSensorsGraphsWithBox from "./chart/EquipmentPartSensorsGraphsWithBox";
import EquipmentPartsReplaceLayer from "../layer/EquipmentPartsReplaceLayer";
import EquipmentPartMeasurementGraphs from "./chart/EquipmentPartMeasurementGraphs";
import EquipmentPartMeasurementDurationGraphs from "./chart/EquipmentPartMeasurementDurationGraphs";

const EquipmentPartListMonitoring = ({
    equipment,
    componentId,
    equipmentAlertIng,
    equipmentParts,
    selectedComponent,
    locationCode,
    target = "default",
    playMovie
}) => {
    const { t, i18n } = useTranslation();

    const [equipmentPartsByComponent, setEquipmentPartsByComponent] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectLocationCode, setSelectLocationCode] = useState();

    const requestRefresh = useCallback(() => {
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (!selectedComponent) return;

        if (locationCode) {
            setSelectLocationCode(locationCode);
        } else {
            setSelectLocationCode(selectedComponent.componentLocationCode);
        }

        setIsLoading(true);
    }, [componentId, selectedComponent, locationCode]);

    useEffect(() => {
        if (equipment && equipment.id && componentId && componentId > 0) {
            if (equipmentParts && equipmentParts.length > 0) {
                const equipmentPartsGroups = equipmentParts.filter(
                    ep => ep.group["masterEquipmentPart.equipmentCtgr.id"] == componentId
                );

                const sorted = [...equipmentPartsGroups].sort((a, b) => {
                    const nameA = a.group["componentLocation.name"];
                    const nameB = b.group["componentLocation.name"];

                    return nameA.localeCompare(nameB, i18n.language);
                });

                if (!locationCode) {
                    setSelectLocationCode(
                        sorted[0]?.group["componentLocation.code"]
                    );
                }

                setEquipmentPartsByComponent(sorted);
            } else {
                setEquipmentPartsByComponent([]);
            }
        }

        setIsLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentId, equipment.id, equipmentParts, isLoading]);

    if (!isLoading) return null;

    return (
        <>
            {/* 위치 선택 */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    px: 1,
                    py: 0.75
                }}
                className="pannel_tab"
            >
                {equipmentPartsByComponent.length > 1 &&
                    equipmentPartsByComponent.map((data, k) => {
                        const code = data.group["componentLocation.code"];
                        const name = data.group["componentLocation.name"];
                        const selected = selectLocationCode === code;

                        return (
                            <Box
                                key={k}
                                onClick={() => setSelectLocationCode(code)}
                                sx={{
                                    cursor: "pointer",
                                    px: 1.5,
                                    py: 0.75,
                                    color: "#fff",
                                    borderBottom: selected
                                        ? "3px solid #0061d0"
                                        : "1px solid #0061d0",
                                    opacity: selected ? 1 : 0.7
                                }}
                            >
                                {name}
                            </Box>
                        );
                    })}
            </Box>

            {/* 선택된 위치의 부품 */}
            {equipmentPartsByComponent.map((data, k) => {
                if (selectLocationCode != data.group["componentLocation.code"]) {
                    return null;
                }

                return (
                    <React.Fragment key={k}>
                        {data.items.map((item, i) => {
                            const componentName = item.masterEquipmentPart.name;
                            const faultCodes = item.faultCodes || [];

                            return (
                                <Box key={i} sx={{ mb: 2 }}>
                                    <MainCard
                                        className="center_table"
                                        content={false}
                                    >
                                        {/* 부품 정보 헤더 */}
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1.75,
                                                backgroundColor: "#111827",
                                                borderRadius: "8px 8px 0 0",
                                                borderBottom: "1px solid rgba(255,255,255,0.12)"
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                spacing={1}
                                            >
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        color: "#f3f4f6",
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    {componentName}
                                                </Typography>

                                                <EquipmentPartsReplaceLayer
                                                    info={equipment}
                                                    type={item.status}
                                                    onRefresh={requestRefresh}
                                                />
                                            </Stack>

                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                flexWrap="wrap"
                                                spacing={1}
                                                sx={{ mt: 0.75 }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#9ca3af" }}
                                                >
                                                    {t("message.common.subtitle.position")}
                                                    :
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#d1d5db" }}
                                                >
                                                    {item.componentLocation?.name || "-"}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#6b7280" }}
                                                >
                                                    ·
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#9ca3af" }}
                                                >
                                                    {t("message.common.subtitle.last.inspection.date")}
                                                    :
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#d1d5db" }}
                                                >
                                                    {equipment.latestCommYmd || "-"}
                                                </Typography>
                                            </Stack>
                                        </Box>

                                        {/* 고장 정보 */}
                                        {equipmentAlertIng && faultCodes.length > 0 && (
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 1.25,
                                                    borderBottom: "1px solid rgba(255,255,255,0.1)"
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems="flex-start"
                                                    spacing={1}
                                                    flexWrap="wrap"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "#fca5a5",
                                                            fontWeight: 600,
                                                            minWidth: 55
                                                        }}
                                                    >
                                                        {t("message.common.title.defect")}
                                                    </Typography>

                                                    <Stack spacing={0.5}>
                                                        {faultCodes.map((codes, idx) => (
                                                            <Stack
                                                                key={idx}
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={1}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: "#d1d5db" }}
                                                                >
                                                                    {codes.subCategoryNm}({codes.categoryNm})
                                                                </Typography>

                                                                {target === "default" && playMovie && (
                                                                    <Box
                                                                        component="span"
                                                                        onClick={() => {
                                                                            playMovie(
                                                                                item?.masterEquipmentPart?.code,
                                                                                item?.componentLocation?.code,
                                                                                codes.code
                                                                            );
                                                                        }}
                                                                        sx={{
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            cursor: "pointer"
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/img/icon/emergency_recording.png`}
                                                                            alt=""
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        )}

                                        {/* 부품 연계 그래프 */}
                                        {item.id && (
                                            <EquipmentPartChainGraphs
                                                equipment={equipment}
                                                equipmentPartId={item.id}
                                                diagnosisResultChain={item.diagnosisResultChain}
                                                target={target}
                                            />
                                        )}

                                        {/* 센서별 그래프 */}
                                        {item.sensorPositions &&
                                            item.id &&
                                            item.sensorPositions.length > 0 &&
                                            item.sensorPositions.map((sPosition) => (
                                                <EquipmentPartSensorsGraphsWithBox
                                                    key={`sensor${sPosition.id}`}
                                                    equipment={equipment}
                                                    equipmentPartId={item.id}
                                                    equipmentPartPositionName={item.componentLocation?.name}
                                                    sensorPosition={sPosition}
                                                    target={target}
                                                />
                                            ))}

                                        {/* 가동상태 그래프 */}
                                        <EquipmentPartMeasurementGraphs
                                            key={`${item.componentLocation.id}_state`}
                                            equipmentPartId={item.id}
                                            componentLocationId={item.componentLocation?.id}
                                        />

                                        {/* 가동시간 그래프 */}
                                        <EquipmentPartMeasurementDurationGraphs
                                            key={`${item.componentLocation.id}_duration`}
                                            equipmentPartId={item.id}
                                            equipmentPartName={item.masterEquipmentPart.name}
                                            componentLocationId={item.componentLocation?.id}
                                        />

                                        <Box className="divide2" />
                                    </MainCard>
                                </Box>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </>
    );
};

export default EquipmentPartListMonitoring;
